import { useState, useLayoutEffect, useRef } from "react";

import classnames from "classnames";
import { copyToClipboard } from "neetocommons/utils/general";
import { Copy, Check } from "neetoicons";
import { Button } from "neetoui";
import PropTypes from "prop-types";
import { mergeAll } from "ramda";
import { useTranslation } from "react-i18next";

import {
  TOOLTIP_CONFIG,
  TIME_OUT,
  BUTTON_STYLES,
  BUTTON_SIZES,
} from "./constants";
import "./copyToClipboard.scss";

const CopyToClipboardButton = ({
  className,
  icon = Copy,
  label = "",
  successLabel,
  tooltipContent,
  successTooltipContent,
  value,
  style,
  size = BUTTON_SIZES.medium,
  tooltipProps,
  onClick,
  ...otherProps
}) => {
  const { t } = useTranslation();

  const [isChecked, setIsChecked] = useState(false);

  const ref = useRef();

  const onHandleClick = event => {
    event.preventDefault();
    event.stopPropagation();

    if (onClick) onClick(event);
    else copyToClipboard(value, { showToastr: false });

    setIsChecked(true);
    setTimeout(() => {
      setIsChecked(false);
    }, TIME_OUT); // Reset copied state after 2 seconds
  };

  useLayoutEffect(() => {
    if (!ref.current) return;
    ref.current.style.minWidth = "";
    const buttonWidth = ref.current.clientWidth;
    ref.current.style.minWidth = `${buttonWidth}px`;
  }, [size]);

  const tooltipOptions = label
    ? null
    : mergeAll([
        TOOLTIP_CONFIG,
        {
          content: isChecked
            ? successTooltipContent || t("neetoMolecules.common.copied")
            : tooltipContent || t("neetoMolecules.common.copy"),
        },
        tooltipProps,
      ]);

  const buttonLabel =
    isChecked && label
      ? successLabel || t("neetoMolecules.common.copied")
      : label;
  const buttonIcon = isChecked ? Check : icon;

  return (
    <Button
      {...{ ref, size, style }}
      data-testid="copy-to-clipboard-button"
      icon={buttonIcon}
      label={buttonLabel}
      tooltipProps={tooltipOptions}
      className={classnames("neeto-molecules-copy-button", {
        [className]: className,
        "neeto-molecules-copy-button--active": isChecked,
        [`neeto-molecules-copy-button--${size}`]: size,
      })}
      onClick={onHandleClick}
      {...otherProps}
    />
  );
};

CopyToClipboardButton.propTypes = {
  /**
   * To provide additional classnames to the button.
   */
  className: PropTypes.string,
  /**
   * To provide the icon to be passed to the button. Defaults to the Copy icon.
   */
  icon: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  /**
   * To provide the label to the button. By default there is no label.
   */
  label: PropTypes.string,
  /**
   * The label of button when the value is copied to clipboard. Defaults to 'Copied'.
   */
  successLabel: PropTypes.string,
  /**
   * The value to be copied to clipboard. (required)
   */
  value: PropTypes.string,
  /**
   * To specify the style of the Button.
   */
  style: PropTypes.oneOf(Object.values(BUTTON_STYLES)),
  /**
   * To specify the size of the Button. Defaults to 'medium'.
   */
  size: PropTypes.oneOf(Object.values(BUTTON_SIZES)),
  /**
   * The content of the tooltip when the value is copied to clipboard. Defaults to 'Copy'.
   */
  tooltipContent: PropTypes.string,
  /**
   * The content of the tooltip when the value is copied to clipboard. Defaults to 'Copied'.
   */
  successTooltipContent: PropTypes.string,
};

export default CopyToClipboardButton;
